// @flow
import React, { useContext, useEffect } from 'react';
import { isEmpty, get, has } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';

import ReactAppContext from 'Context/ReactAppContext';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader.jsx';
import { useRouter } from 'Client/router/useRouter';
import {
  getCalendar,
  getDays,
  getSelectedMonth,
  getCalendarLoading,
} from 'Store/calendar/calendar.selector';
import {
  fetchCalendar,
  setPossibleDepartureDate,
  selectMonth,
} from 'Store/calendar/calendar.action';
import { type CalendarData } from 'Store/calendar/calendar.helper';
import { getDepartureDate } from 'Store/trip-date-criteria/storeHelper';
import PriceCalendarMonths from './months/PriceCalendarMonths.jsx';
import PriceCalendarDays from './days/PriceCalendarDays.jsx';

type Props = {
  resortId: string,
  bookingConfig: {
    enableTransfer: ?boolean,
    form: {
      title: ?string,
      dateFormat: ?string,
      dateValidate: ?string,
      selectDuration: ?string,
      durationTitle: ?string,
      selectChildrenLabels: ?*,
      selectAdultsLabels: ?*,
      errorMaxGuestsLabel: ?string,
    },
    priceCalendar: {
      offers: {
        urgentisation: {
          label: string,
          rateToDisplay: number,
        },
      },
    },
  },
  travelAdvisorConfig: ?*,
  cmsPrice: ?*,
  cmsStartingPrice: ?*,
  transport: string,
  onSelectDate: (*) => void,
  didMountCallback: (*) => void,
  isJoyview: boolean,
  duration: number,
};

export const PriceCalendar = (props: Props) => {
  const appContext = useContext(ReactAppContext);
  const { location } = useRouter<UrlParams, *>();
  const { queryParams } = location;
  const { bookingConfig, resortId, transport, didMountCallback, duration } = props;
  const urgentisationRate = bookingConfig.priceCalendar.offers.urgentisation.rateToDisplay;
  const dispatch = useDispatch();
  const calendar = useSelector<CalendarData>(getCalendar);
  const days = useSelector<Array<*>>(getDays);
  const selectedMonth = useSelector<number>(getSelectedMonth);
  const selectedDay = useSelector<number>(getDepartureDate);
  const isLoading = useSelector<boolean>(getCalendarLoading);

  useEffect(
    () => {
      const hasProposalIdQueryParam = has(queryParams, 'proposalId');

      if (hasProposalIdQueryParam) return; // because a Redux action will fetch the calendar

      dispatch(fetchCalendar({ resortId, transport, duration, urgentisationRate }, appContext));
    },
    [resortId, transport, duration, urgentisationRate],
  );

  useEffect(
    () => {
      dispatch(setPossibleDepartureDate());
      /**
       * Should fetch proposals after state update for arrivaldate
       * see BookingEngineForm, ligne 170.
       * */
      didMountCallback && didMountCallback();
    },
    [calendar.months],
  );

  /**
   * make sure the selected date is up to date after a transport update
   */
  useEffect(
    () => {
      if (selectedDay) {
        const day = days.find(calendarDay => {
          return calendarDay.dayContent.id === selectedDay;
        });

        day && onSelectDate(day.dayContent);
      }
    },
    [calendar, transport, selectedDay],
  );

  const onSelectMonth = monthId => {
    dispatch(selectMonth(monthId));
  };

  const onSelectDate = day => {
    props.onSelectDate(day);
  };

  return (
    <div className="PriceCalendar relative" id="calendar">
      {isLoading && (
        <div className="absolute inset-x-0 inset-y-0 z-1 bg-gray-lighter">
          <AsyncLoader />
        </div>
      )}
      <div>
        {!isEmpty(calendar) ? (
          <React.Fragment>
            <PriceCalendarMonths
              blocks={calendar.blocks}
              cmsPrice={props.cmsPrice}
              currentMonth={selectedMonth}
              onChangeMonth={onSelectMonth}
            />
            <PriceCalendarDays
              days={days}
              currentDay={selectedDay}
              currentMonth={selectedMonth}
              onChangeDay={onSelectDate}
              onChangeMonth={onSelectMonth}
              cmsStartingPrice={props.cmsStartingPrice}
              cmsPrice={props.cmsPrice}
              weekDayLongNames={get(calendar, 'weekDays')}
              bookingConfig={props.bookingConfig}
              travelAdvisorConfig={props.travelAdvisorConfig}
              isJoyview={props.isJoyview}
              duration={parseInt(queryParams.duration, 10) || props.duration}
              shouldScrollOnDaySelection
            />
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
};
